/*!
 * JM Live Preview v1.6 — product personalisation preview for Shopify
 * Jewelled Me · no dependencies · works with Be Yours and other Online Store 2.0 themes
 */
(function () {
  'use strict';

  var BLANK = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
  var instances = (window.JMLivePreview = window.JMLivePreview || {});

  /* ---------- small helpers ---------- */
  function lines(text) {
    return String(text || '')
      .split(/\r?\n|\s\/\s/)
      .map(function (l) { return l.trim(); })
      .filter(Boolean);
  }

  function parseMonths(text) {
    return lines(text).map(function (l) {
      var p = l.split('|').map(function (s) { return s.trim(); });
      return { key: (p[0] || '').toLowerCase(), month: p[1] || p[0], stone: p[2] || '', color: p[3] || '#cccccc' };
    }).filter(function (m) { return m.key; });
  }

  function parseLayout(text) {
    var out = {};
    lines(text).forEach(function (l) {
      var m = l.split('=');
      if (m.length !== 2) return;
      var n = parseInt(m[0], 10);
      if (!n) return;
      out[n] = m[1].split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    });
    return out;
  }

  function parseMetalMap(text) {
    return lines(text).map(function (l) {
      var m = l.split('=');
      if (m.length !== 2) return null;
      try { return { re: new RegExp(m[0].trim(), 'i'), key: m[1].trim().toLowerCase() }; } catch (e) { return null; }
    }).filter(Boolean);
  }

  function slug(s) {
    return String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  function el(tag, cls, attrs) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (attrs) Object.keys(attrs).forEach(function (k) { n.setAttribute(k, attrs[k]); });
    return n;
  }

  // Load an image off-screen first so the visible layer never flashes empty.
  var loadCache = {};
  function preload(url) {
    if (!url) return Promise.reject();
    if (!loadCache[url]) {
      loadCache[url] = new Promise(function (resolve, reject) {
        var i = new Image();
        i.crossOrigin = 'anonymous';
        i.decoding = 'async';
        i.onload = function () { resolve(url); };
        i.onerror = function () { delete loadCache[url]; reject(url); };
        i.src = url;
      });
    }
    return loadCache[url];
  }

  /* ---------- main class ---------- */
  function Preview(root) {
    this.root = root;
    this.id = root.getAttribute('data-section-id');
    this.cfg = JSON.parse(root.querySelector('[data-jmlp-config]').textContent);
    this.months = parseMonths(this.cfg.months);
    this.layout = parseLayout(this.cfg.slotLayout);
    this.metalMap = parseMetalMap(this.cfg.metalMap);
    this.stage = root.querySelector('[data-jmlp-stage]');
    this.stageWrap = root.querySelector('[data-jmlp-stage-wrap]');
    this.controls = root.querySelector('[data-jmlp-controls]');
    this.base = root.querySelector('[data-jmlp-base]');
    this.layersEl = root.querySelector('[data-jmlp-layers]');
    this.loading = root.querySelector('[data-jmlp-loading]');
    this.stonesEl = root.querySelector('[data-jmlp-stones]');
    this.countWrap = root.querySelector('[data-jmlp-count-wrap]');
    this.countEl = root.querySelector('[data-jmlp-count]');
    this.textIn = root.querySelector('[data-jmlp-text-in]');
    this.textOut = root.querySelector('[data-jmlp-text-out]');
    this.textCount = root.querySelector('[data-jmlp-text-count]');
    this.openRow = 0;
    this.renderToken = 0;

    var opts = (this.cfg.product && this.cfg.product.options) || [];
    this.metalIdx = this.findOption(opts, this.cfg.metalOptionPattern);
    this.monthIdx = this.findMonthOption(opts);
    this.countIdx = this.findOption(opts, this.cfg.countOptionPattern, true);
    // month photos from the product's media (1.Jana.png = gold, 1.Janb.png = silver): no Birthstone option needed
    this.mediaPhotos = this.parseMediaPhotos();
    this.split = this.cfg.layout === 'split';
    this.photoMode = this.cfg.mode === 'variant' &&
      (this.monthIdx >= 0 || !!this.mediaPhotos.found || (this.split && !!this.cfg.photos));
    if (this.split) {
      this.countIdx = -1; this.cfg.maxStones = 2;
      // per-product split settings (metafield "Live preview: split settings"), e.g.
      // {"at":0.535,"mirror":false,"stone":[0.08,0.51,0.09],"path":[[0,0.542],[0.49,0.538],[0.6,0.517],[1,0.513]]}
      var sc = this.cfg.splitCfg || {};
      if (sc.at) this.cfg.splitAt = sc.at;
      if (sc.stone) this.cfg.splitStone = sc.stone;
      this.cfg.splitPath = sc.path || null;
      this.cfg.splitMirror = sc.mirror !== false;
    }

    this.state = {
      variantId: this.cfg.product && this.cfg.product.selected,
      count: this.split ? 2 : Math.min(this.cfg.defaultCount || 1, this.cfg.maxStones || 1),
      stones: this.initialStones(),
      metal: null,
      text: ''
    };
    this.applyDesignParam();

    this.forms = this.findForms();
    this.form = this.forms[0] || null;
    this.relocate();
    if (this.cfg.mode === 'variant' && this.monthIdx >= 0 && this.cfg.hideThemeMonth !== false) this.hideThemeMonth();
    this.buildCount();
    this.bindText();
    this.bindForm();
    this.syncFromVariant(true);
    instances[this.id] = this;
  }

  // Finds one-pendant month photos in the product media.
  // JM naming: "{month number}.{Mon}{code}" e.g. 1.Jana_….png / 12.Decb.png, code letter -> metal.
  // Also accepts alt text such as "January – Gold" (month name + metal word).
  Preview.prototype.parseMediaPhotos = function () {
    var self = this, out = { found: 0, byMetal: {} };
    var codes = {};
    lines(this.cfg.mediaMetalCodes).forEach(function (l) {
      var m = l.split('=');
      if (m.length === 2) codes[m[0].trim().toLowerCase()] = m[1].trim().toLowerCase();
    });
    var media = (this.cfg.product && this.cfg.product.media) || [];
    media.forEach(function (md) {
      if (!md || !md.src) return;
      var file = decodeURIComponent(md.src.split('?')[0].split('/').pop());
      var mIdx = -1, metal = null;
      var m = file.match(/^(\d{1,2})\.\s*([a-z]{3})([a-z])?(?=[_.\-\s])/i);
      if (m) {
        var num = parseInt(m[1], 10) - 1;
        var byName = self.monthIndexOf(m[2]);
        mIdx = byName >= 0 ? byName : (num >= 0 && num < self.months.length ? num : -1);
        if (m[3]) metal = codes[m[3].toLowerCase()] || null;
      } else if (md.alt) {
        var alt = md.alt.toLowerCase();
        for (var i = 0; i < self.months.length; i++) {
          if (new RegExp('\\b' + self.months[i].month.toLowerCase() + '\\b').test(alt)) { mIdx = i; break; }
        }
        if (mIdx >= 0) for (var k = 0; k < self.metalMap.length; k++) if (self.metalMap[k].re.test(alt)) { metal = self.metalMap[k].key; break; }
      }
      if (mIdx < 0) return;
      var key = metal || '_any';
      out.byMetal[key] = out.byMetal[key] || {};
      if (!out.byMetal[key][mIdx]) { out.byMetal[key][mIdx] = md.src; out.found++; }
    });
    // only worth using if most months are covered
    var best = 0;
    Object.keys(out.byMetal).forEach(function (k) { best = Math.max(best, Object.keys(out.byMetal[k]).length); });
    if (best < Math.min(6, this.months.length)) out.found = 0;
    return out;
  };

  // ?design=oct.nov.dec opens the page with those stones picked (used by the order "design" link)
  Preview.prototype.applyDesignParam = function () {
    var q = new URLSearchParams(location.search).get('design');
    if (!q) return;
    var parts = q.split('|'); // "silver|4|oct.nov.dec|" (the hidden _preview code) or just "oct.nov.dec"
    var keys = (parts.length >= 3 ? parts[2] : parts[0]).split('.');
    var self = this, n = 0;
    keys.forEach(function (k, i) {
      for (var m = 0; m < self.months.length; m++) if (self.months[m].key === k) { self.state.stones[i] = m; n++; }
    });
    if (n && !this.split && this.countIdx < 0) this.state.count = Math.min(n, this.cfg.maxStones || n);
  };

  // A file in Content > Files named by a pattern, e.g. "larme-pendant-{metal}-{mon}.png".
  // Tokens: {metal} gold/silver, {mon} jan, {month} january, {Mon} Jan, {n} 1-12, {pair} jan-feb, {pairn} 1-2
  Preview.prototype.fileUrl = function (pattern, metal, mIdx, width, reversed) {
    var m = this.months[mIdx];
    if (!pattern || !m) return null;
    var a = mIdx - (mIdx % 2), b = Math.min(a + 1, this.months.length - 1);
    var name = pattern
      .replace(/\{metal\}/g, metal || 'gold')
      .replace(/\{mon\}/g, m.key)
      .replace(/\{Mon\}/g, m.key.charAt(0).toUpperCase() + m.key.slice(1))
      .replace(/\{month\}/g, m.month.toLowerCase())
      .replace(/\{n\}/g, String(mIdx + 1))
      .replace(/\{pair\}/g, reversed ? this.months[b].key + '-' + this.months[a].key : this.months[a].key + '-' + this.months[b].key)
      .replace(/\{pairn\}/g, reversed ? (b + 1) + '-' + (a + 1) : (a + 1) + '-' + (b + 1));
    return this.cfg.filesRoot + encodeURI(name) + '?width=' + (width || 1200);
  };

  Preview.prototype.optionValues = function (idx) {
    var seen = {};
    ((this.cfg.product && this.cfg.product.variants) || []).forEach(function (v) { seen[v.options[idx]] = 1; });
    return Object.keys(seen);
  };

  Preview.prototype.findOption = function (opts, pattern, numeric) {
    if (!pattern) return -1;
    var re;
    try { re = new RegExp(pattern, 'i'); } catch (e) { return -1; }
    for (var i = 0; i < opts.length; i++) {
      var name = typeof opts[i] === 'string' ? opts[i] : opts[i].name;
      if (!re.test(name) || i === this.monthIdx) continue;
      // a stone-count option must hold counts ("3 Stones", "Trio"), not months
      if (numeric && !this.optionValues(i).some(function (v) { return /\d|single|one|two|duo|trio|three/i.test(v); })) continue;
      return i;
    }
    return -1;
  };

  // A variant option whose values are months (e.g. "Birthstone: January") drives stone 1.
  Preview.prototype.monthIndexOf = function (value) {
    var v = String(value || '').trim().toLowerCase();
    for (var i = 0; i < this.months.length; i++) {
      var m = this.months[i];
      if (v === m.month.toLowerCase() || v === m.key || v === m.stone.toLowerCase() ||
          v.indexOf(m.month.toLowerCase()) === 0) return i;
    }
    return -1;
  };

  Preview.prototype.findMonthOption = function (opts) {
    for (var i = 0; i < opts.length; i++) {
      var vals = this.optionValues(i);
      var hits = vals.filter(function (v) { return this.monthIndexOf(v) >= 0; }, this).length;
      if (vals.length >= 6 && hits / vals.length > 0.8) return i;
    }
    return -1;
  };

  // Click the theme's own option input so price, stock and variant id stay correct.
  Preview.prototype.selectVariantMonth = function (mIdx) {
    if (this.monthIdx < 0) return;
    var target = this.optionValues(this.monthIdx).filter(function (v) { return this.monthIndexOf(v) === mIdx; }, this)[0];
    if (!target || !this.form) return;
    var scope = this.form.closest('section, .shopify-section') || document;
    var inputs = scope.querySelectorAll('input[type="radio"], select');
    for (var i = 0; i < inputs.length; i++) {
      var n = inputs[i];
      if (n.tagName === 'SELECT') {
        for (var k = 0; k < n.options.length; k++) {
          if (n.options[k].value === target) {
            if (n.value !== target) { n.value = target; n.dispatchEvent(new Event('change', { bubbles: true })); }
            return;
          }
        }
      } else if (n.value === target) {
        if (!n.checked) n.click();
        return;
      }
    }
  };

  // Stone 1 already controls the month variant, so the theme's own month dropdown is redundant.
  // It is hidden (not removed) so the theme keeps using it behind the scenes.
  Preview.prototype.hideThemeMonth = function () {
    if (!this.form) return;
    var vals = this.optionValues(this.monthIdx);
    var scope = this.form.closest('section, .shopify-section') || document;
    var nodes = scope.querySelectorAll('select, input[type="radio"]');
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i], hit = false;
      if (n.tagName === 'SELECT') {
        hit = Array.prototype.some.call(n.options, function (o) { return vals.indexOf(o.value) >= 0; }) &&
              n.options.length <= vals.length + 1;
      } else {
        hit = vals.indexOf(n.value) >= 0;
      }
      if (!hit) continue;
      var wrap = n.closest('.product-form__input, fieldset') || n.parentElement;
      if (wrap && !this.controls.contains(wrap)) wrap.style.display = 'none';
      return;
    }
  };

  Preview.prototype.initialStones = function () {
    var n = this.months.length || 12;
    var max = Math.max(this.cfg.maxStones || 1, 12);
    var start = this.cfg.defaultSelection === 'january' ? 0 : new Date().getMonth();
    var out = [];
    for (var i = 0; i < max; i++) {
      out.push(this.cfg.defaultSelection === 'random' ? Math.floor(Math.random() * n) : (start + i) % n);
    }
    return out;
  };

  /* ----- theme integration ----- */
  // All add-to-cart forms for this product (main form + sticky bar), skipping Shop Pay instalment forms.
  Preview.prototype.findForms = function () {
    var self = this;
    var sel = this.cfg.formSelector;
    if (sel) {
      var picked = Array.prototype.slice.call(document.querySelectorAll(sel)).map(function (n) {
        return n.tagName === 'FORM' ? n : n.querySelector('form');
      }).filter(Boolean);
      if (picked.length) return picked;
    }
    var forms = Array.prototype.filter.call(document.querySelectorAll('form[action*="/cart/add"]'), function (f) {
      if (/installment/i.test(f.className) || f.closest('shopify-payment-terms')) return false;
      var idInput = f.querySelector('[name="id"]');
      return idInput && self.variantById(idInput.value);
    });
    // the main form first (inside <product-form>, not the sticky bar)
    forms.sort(function (a, b) {
      var sa = /sticky/i.test((a.parentElement && a.parentElement.className) || '') ? 1 : 0;
      var sb = /sticky/i.test((b.parentElement && b.parentElement.className) || '') ? 1 : 0;
      return sa - sb;
    });
    return forms;
  };

  Preview.prototype.relocate = function () {
    var self = this;
    var cls = 'jmlp-moved-' + this.id;
    if (this.cfg.gallerySelector) {
      var host = document.querySelector(this.cfg.gallerySelector);
      var list = host && host.querySelector('.product__media-list, [id^="Slider-Gallery"]');
      this.stageWrap.classList.add(cls, 'jmlp__stage-wrap--hosted');
      if (list) {
        // keep the theme gallery: the preview becomes the first slide, the other photos stay scrollable
        this.galleryList = list;
        this.slide = el('li', 'product__media-item flex-grid__item slider__slide jmlp-slide is-active', { 'data-jmlp-slide': '' });
        this.slide.appendChild(this.stageWrap);
        list.insertBefore(this.slide, list.firstChild);
        this.thumbList = host.querySelector('.thumbnail-list, [id^="Slider-Thumbnails"]');
        if (this.thumbList) {
          this.thumb = el('li', 'thumbnail-list__item slider__slide jmlp-thumb', { 'data-jmlp-thumb': '' });
          var btn = el('button', 'jmlp-thumb__btn', { type: 'button', 'aria-label': 'Show your design' });
          this.thumbCanvas = el('canvas', 'jmlp-thumb__canvas');
          btn.appendChild(this.thumbCanvas);
          btn.addEventListener('click', function () { self.showSlide(); });
          this.thumb.appendChild(btn);
          this.thumbList.insertBefore(this.thumb, this.thumbList.firstChild);
        }
        this.hideVariantMedia();
        // only a real click / tap / key press in the gallery may move the customer away from the design;
        // anything the theme does on its own (page load, variant change) brings the design back
        var mark = function () { self._userAt = Date.now(); };
        ['pointerdown', 'touchstart', 'keydown'].forEach(function (ev) { host.addEventListener(ev, mark, { capture: true, passive: true }); });
        [300, 1000, 2500].forEach(function (ms) { setTimeout(function () { if (!self._userAt) self.activateSlide(); }, ms); });
        // the theme may rebuild or reorder the gallery when the variant changes: put the slide back if so
        new MutationObserver(function () {
          if (self.slide && !self.slide.isConnected) {
            var l = host.querySelector('.product__media-list, [id^="Slider-Gallery"]');
            if (l) { self.galleryList = l; l.insertBefore(self.slide, l.firstChild); }
          }
          if (self.thumb && !self.thumb.isConnected) {
            var t = host.querySelector('.thumbnail-list, [id^="Slider-Thumbnails"]');
            if (t) { self.thumbList = t; t.insertBefore(self.thumb, t.firstChild); }
          }
          self.hideVariantMedia();
          // the theme tried to show a month photo (which is hidden): show the live design instead
          var l2 = self.galleryList;
          if (l2 && l2.querySelector('[data-jmlp-hidden].is-active')) { self.activateSlide(); return; }
          var other = l2 && l2.querySelector('.product__media-item.is-active:not([data-jmlp-slide]):not([data-jmlp-hidden])');
          if (!other || other === self._userPick) return;
          if (Date.now() - (self._userAt || 0) < 1500) {
            // the customer opened another photo (thumbnail / arrow / swipe): step the live design aside
            self._userPick = other;
            if (self.slide.classList.contains('is-active')) {
              self.slide.classList.remove('is-active');
              if (self.thumb) self.thumb.firstChild.removeAttribute('aria-current');
            }
          } else if (Date.now() - (self._activatedAt || 0) > 50) {
            self.activateSlide(); // the theme switched photo by itself: keep the design in front
          }
        }).observe(host, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
      } else if (host) {
        host.classList.add('jmlp-host');
        host.insertBefore(this.stageWrap, host.firstChild);
      }
    }
    if (this.cfg.controlsSelector) {
      var anchor = document.querySelector(this.cfg.controlsSelector);
      if (anchor && anchor.parentNode) {
        this.controls.classList.add(cls, 'jmlp__controls--hosted');
        anchor.parentNode.insertBefore(this.controls, anchor);
      }
    }
    if (this.root.querySelectorAll('.jmlp__stage-wrap, .jmlp__controls').length === 0) {
      this.root.hidden = true; // everything moved into the theme - collapse the empty section
    }
    this.buildMini(cls);
  };

  // Month photos are what the preview is built from, so their own slides/thumbnails would just repeat it.
  Preview.prototype.hideVariantMedia = function () {
    if (!this.galleryList || !this.photoMode) return;
    var names = {};
    function fname(u) { return decodeURIComponent(String(u).split('?')[0].split('/').pop()); }
    if (this.monthIdx >= 0) (this.cfg.product.variants || []).forEach(function (v) {
      if (v.image) names[fname(v.image)] = 1;
    });
    var bm = this.mediaPhotos.found ? this.mediaPhotos.byMetal : {};
    Object.keys(bm).forEach(function (k) { Object.keys(bm[k]).forEach(function (i) { names[fname(bm[k][i])] = 1; }); });
    function isVariantImg(item) {
      var img = item.querySelector('img');
      if (!img) return false;
      var srcs = [img.currentSrc, img.src, img.getAttribute('data-src'), (img.getAttribute('srcset') || '').split(' ')[0]];
      for (var i = 0; i < srcs.length; i++) if (srcs[i] && names[fname(srcs[i])]) return true;
      return false;
    }
    [this.galleryList, this.thumbList].forEach(function (list) {
      if (!list) return;
      Array.prototype.forEach.call(list.children, function (li) {
        if (li.hasAttribute('data-jmlp-slide') || li.hasAttribute('data-jmlp-thumb')) return;
        if (isVariantImg(li)) { li.style.display = 'none'; li.setAttribute('data-jmlp-hidden', ''); }
      });
    });
  };

  // Make the live design the visible gallery item. On desktop the theme shows only the one item
  // marked .is-active, on mobile it scrolls a strip, so do both.
  Preview.prototype.activateSlide = function (smooth) {
    var l = this.galleryList, sl = this.slide;
    if (!l || !sl) return;
    if (l.firstElementChild !== sl) l.insertBefore(sl, l.firstChild);
    Array.prototype.forEach.call(l.querySelectorAll('.is-active'), function (li) { if (li !== sl) li.classList.remove('is-active'); });
    sl.classList.add('is-active');
    this._activatedAt = Date.now();
    this._userPick = null;
    if (this.thumbList) {
      Array.prototype.forEach.call(this.thumbList.querySelectorAll('[aria-current]'), function (n) { n.removeAttribute('aria-current'); });
      Array.prototype.forEach.call(this.thumbList.querySelectorAll('.is-active'), function (n) { n.classList.remove('is-active'); });
      if (this.thumb) this.thumb.firstChild.setAttribute('aria-current', 'true');
    }
    l.scrollTo({ left: sl.offsetLeft - l.offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
  };

  Preview.prototype.showSlide = function () {
    this.activateSlide(true);
    var r = this.stage.getBoundingClientRect();
    if (r.top < 0 || r.bottom > window.innerHeight) this.stage.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  // Small floating copy of the design, shown while the customer is choosing stones lower down the page.
  Preview.prototype.buildMini = function (cls) {
    var self = this;
    if (!this.photoMode || !('IntersectionObserver' in window)) return;
    this.mini = el('button', 'jmlp-mini ' + cls, { type: 'button', 'aria-label': 'Back to your design', hidden: '' });
    this.miniCanvas = el('canvas', 'jmlp-mini__canvas');
    this.mini.appendChild(this.miniCanvas);
    this.mini.addEventListener('click', function () { self.showSlide(); });
    document.body.appendChild(this.mini);
    var stageVisible = true, controlsVisible = false;
    function update() {
      var show = !stageVisible && controlsVisible;
      if (show) {
        var hdr = document.querySelector('sticky-header, .header-wrapper, .shopify-section-header-sticky, header');
        var b = hdr ? hdr.getBoundingClientRect().bottom : 0;
        self.mini.style.top = Math.max(8, b + 8) + 'px';
      }
      self.mini.hidden = !show;
    }
    new IntersectionObserver(function (e) { stageVisible = e[0].isIntersecting; update(); }, { threshold: 0.25 }).observe(this.stage);
    new IntersectionObserver(function (e) { controlsVisible = e[0].isIntersecting; update(); }, { threshold: 0 }).observe(this.controls);
    window.addEventListener('scroll', function () { if (!self.mini.hidden) update(); }, { passive: true });
  };

  Preview.prototype.paintCopies = function () {
    var src = this.canvas, f = this.focus;
    if (!src || !f) return;
    [[this.miniCanvas, 240], [this.thumbCanvas, 200]].forEach(function (pair) {
      var c = pair[0];
      if (!c) return;
      c.width = c.height = pair[1];
      c.getContext('2d').drawImage(src, f.x, f.y, f.s, f.s, 0, 0, pair[1], pair[1]);
    });
  };

  Preview.prototype.variantById = function (id) {
    var vs = (this.cfg.product && this.cfg.product.variants) || [];
    for (var i = 0; i < vs.length; i++) if (String(vs[i].id) === String(id)) return vs[i];
    return null;
  };

  Preview.prototype.currentVariantId = function () {
    var input = this.form && this.form.querySelector('[name="id"]');
    if (input && input.value) return input.value;
    var u = new URLSearchParams(location.search).get('variant');
    return u || this.state.variantId;
  };

  Preview.prototype.metalFromVariant = function (v) {
    if (!v || this.metalIdx < 0) return null;
    var val = v.options[this.metalIdx] || '';
    for (var i = 0; i < this.metalMap.length; i++) if (this.metalMap[i].re.test(val)) return this.metalMap[i].key;
    return slug(val);
  };

  Preview.prototype.countFromVariant = function (v) {
    if (!v || this.countIdx < 0) return null;
    var val = String(v.options[this.countIdx] || '');
    var m = val.match(/\d+/);
    if (m) return parseInt(m[0], 10);
    var words = { one: 1, single: 1, two: 2, double: 2, duo: 2, three: 3, trio: 3, four: 4, five: 5, six: 6 };
    var w = val.toLowerCase().match(/[a-z]+/g) || [];
    for (var i = 0; i < w.length; i++) if (words[w[i]]) return words[w[i]];
    return null;
  };

  Preview.prototype.syncFromVariant = function (force) {
    var id = this.currentVariantId();
    if (!force && String(id) === String(this.state.variantId)) return;
    this.state.variantId = id;
    var v = this.variantById(id);
    this.state.metal = this.metalFromVariant(v);
    var c = this.countFromVariant(v);
    if (c && !this.split) this.state.count = Math.max(1, Math.min(c, 12));
    if (v && this.monthIdx >= 0) {
      var mi = this.monthIndexOf(v.options[this.monthIdx]);
      if (mi >= 0) this.state.stones[0] = mi;
    }
    if (!force && this.galleryList && this.slide) {
      var me = this;
      setTimeout(function () { me.activateSlide(); }, 120);
      setTimeout(function () { me.activateSlide(); }, 450);
    }
    this.countWrap.hidden = this.split || this.countIdx >= 0 || (this.cfg.maxStones || 1) < 2;
    this.updateCountButtons();
    this.buildRows();
    this.render();
  };

  Preview.prototype.bindForm = function () {
    var self = this;
    // Themes swap the variant id in different ways — listen for changes and also poll cheaply.
    document.addEventListener('change', function () { setTimeout(function () { self.syncFromVariant(false); }, 60); });
    setInterval(function () { self.syncFromVariant(false); }, 500);

    this.propsBoxes = this.forms.map(function (form) {
      var box = el('div', 'jmlp__props', { 'data-jmlp-props': '' });
      box.hidden = true;
      form.appendChild(box);
      form.addEventListener('submit', function (e) {
        if (!self.validate()) {
          e.preventDefault();
          e.stopImmediatePropagation();
          return;
        }
        self.saveThumb();
      }, true);
      return box;
    });
  };

  // Small picture of the design, kept in this browser so the cart can show it instead of the stock photo.
  Preview.prototype.saveThumb = function () {
    try {
      if (!this.canvas || !this.focus || !this.previewCode) return;
      var c = document.createElement('canvas'); c.width = c.height = 300;
      var x = c.getContext('2d'), f = this.focus;
      x.fillStyle = this.cfg.stageBg || '#f2f2f2'; x.fillRect(0, 0, 300, 300);
      x.drawImage(this.canvas, f.x, f.y, f.s, f.s, 0, 0, 300, 300);
      thumbStore.set(this.state.variantId + '|' + this.previewCode, c.toDataURL('image/jpeg', 0.82));
    } catch (e) {}
  };

  Preview.prototype.validate = function () {
    if (this.cfg.textEnabled && this.cfg.textRequired && this.textIn && !this.state.text.trim()) {
      this.textIn.classList.add('is-invalid');
      this.textIn.focus();
      this.textIn.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }
    return true;
  };

  /* ----- controls ----- */
  Preview.prototype.buildCount = function () {
    var self = this;
    var max = this.cfg.maxStones || 1;
    this.countEl.innerHTML = '';
    for (var n = 1; n <= max; n++) {
      (function (n) {
        var b = el('button', 'jmlp__count-btn', { type: 'button', role: 'radio', 'aria-checked': 'false' });
        b.textContent = n;
        b.addEventListener('click', function () {
          self.state.count = n;
          self.updateCountButtons();
          self.buildRows();
          self.render();
          self.activateSlide();
        });
        self.countEl.appendChild(b);
      })(n);
    }
  };

  Preview.prototype.updateCountButtons = function () {
    var c = this.state.count;
    Array.prototype.forEach.call(this.countEl.children, function (b, i) {
      b.setAttribute('aria-checked', String(i + 1 === c));
    });
  };

  Preview.prototype.labelFor = function (mIdx) {
    var m = this.months[mIdx];
    return m ? (m.stone ? m.stone + ' – ' + m.month : m.month) : '';
  };

  // "Stone" -> Stone 1, Stone 2 …   "Left stone|Right stone" -> one name per row
  Preview.prototype.rowLabel = function (i) {
    var l = String(this.cfg.stoneLabel || 'Stone');
    if (l.indexOf('|') >= 0) { var parts = l.split('|'); return (parts[i] || parts[parts.length - 1]).trim(); }
    return l + (this.state.count > 1 ? ' ' + (i + 1) : '');
  };

  Preview.prototype.buildRows = function () {
    var self = this;
    this.stonesEl.innerHTML = '';
    if (this.openRow >= this.state.count) this.openRow = -1;

    for (var i = 0; i < this.state.count; i++) {
      (function (i) {
        var row = el('div', 'jmlp__row');
        var head = el('button', 'jmlp__row-head', { type: 'button', 'aria-expanded': String(self.openRow === i) });
        var sw = el('span', 'jmlp__swatch');
        var lbl = el('span', 'jmlp__row-label');
        var small = el('span', 'jmlp__row-k');
        small.textContent = self.rowLabel(i) + ':';
        var val = el('span', 'jmlp__row-v');
        lbl.appendChild(small);
        lbl.appendChild(val);
        var chev = el('span', 'jmlp__chev', { 'aria-hidden': 'true' });
        head.appendChild(sw);
        head.appendChild(lbl);
        head.appendChild(chev);

        var panel = el('div', 'jmlp__panel', { role: 'radiogroup' });
        panel.hidden = self.openRow !== i;
        self.months.forEach(function (m, mIdx) {
          var opt = el('button', 'jmlp__opt', { type: 'button', role: 'radio', title: self.labelFor(mIdx) });
          var dot = el('span', 'jmlp__swatch');
          self.styleSwatch(dot, mIdx);
          var t = el('span', 'jmlp__opt-t');
          t.innerHTML = '<b></b><small></small>';
          t.firstChild.textContent = m.month.slice(0, 3);
          t.lastChild.textContent = m.stone;
          opt.appendChild(dot);
          opt.appendChild(t);
          opt.addEventListener('click', function () {
            self.state.stones[i] = mIdx;
            if (i === 0) self.selectVariantMonth(mIdx);
            // move on to the next stone automatically — fewer taps on mobile
            self.openRow = i + 1 < self.state.count ? i + 1 : -1;
            self.buildRows();
            self.render();
            self.activateSlide(); // bring the live design back into view if another photo was open
          });
          panel.appendChild(opt);
        });

        head.addEventListener('click', function () {
          self.openRow = self.openRow === i ? -1 : i;
          self.buildRows();
        });

        row.appendChild(head);
        row.appendChild(panel);
        self.stonesEl.appendChild(row);
        self.paintRow(row, i);
      })(i);
    }
  };

  // Swatch = a close-up of the real stone. Taken from an uploaded swatch photo if there is one
  // (Content > Files: "{prefix}-swatch-{month}.png", e.g. larme-swatch-jan.png), otherwise cropped
  // automatically from that month's variant photo. The drawn colour shows until the photo is ready.
  Preview.prototype.styleSwatch = function (node, mIdx) {
    var m = this.months[mIdx] || {};
    node.style.setProperty('--sw', m.color || '#ccc');
    if (!this.photoMode || this.cfg.photoSwatches === false) return;
    this.swatchFor(mIdx).then(function (url) {
      if (!url) return;
      node.style.setProperty('--img', 'url("' + url + '")');
      node.classList.add('jmlp__swatch--img');
    });
  };

  Preview.prototype.swatchFor = function (mIdx) {
    var self = this, m = this.months[mIdx];
    this.swatchCache = this.swatchCache || {};
    if (!m) return Promise.resolve(null);
    var metal = this.state.metal || '';
    var ck = metal + ':' + m.key;
    if (!this.swatchCache[ck]) {
      var t = this.cfg.photoThreshold;
      var root = this.cfg.filesRoot + this.cfg.prefix + '-swatch-';
      var fromVariant = this.variantImage(this.state.metal, mIdx) || this.variantImage(null, mIdx);
      // 1) close-up pendant cut-out  2) larme-swatch-silver-jan.png  3) larme-swatch-jan.png  4) cropped from the month photo
      var pend = this.cfg.pendants ? this.fileUrl(this.cfg.pendants, metal, mIdx, 800) : null;
      var first = (pend ? swatchFromUpload(pend) : Promise.reject())
        .catch(function () { return metal ? swatchFromUpload(root + metal + '-' + m.key + '.png?width=800') : Promise.reject(); });
      if (this.split && !pend) {
        var sp = this.splitPhotoFor(metal, mIdx);
        first = first.catch(function () { return sp ? photoPlain(sp.url).then(function (c) { return splitSwatch(c, sp.side, self.cfg.splitAt, self.cfg.splitStone); }) : Promise.reject(); });
      }
      this.swatchCache[ck] = first
        .catch(function () { return swatchFromUpload(root + m.key + '.png?width=800'); })
        .catch(function () { return fromVariant ? photo(fromVariant, t).then(swatchFromVariant) : null; })
        .catch(function () { return null; });
    }
    return this.swatchCache[ck];
  };

  Preview.prototype.paintRow = function (row, i) {
    var mIdx = this.state.stones[i];
    var m = this.months[mIdx] || {};
    this.styleSwatch(row.querySelector('.jmlp__row-head .jmlp__swatch'), mIdx);
    row.querySelector('.jmlp__row-v').textContent = this.labelFor(mIdx);
    Array.prototype.forEach.call(row.querySelectorAll('.jmlp__opt'), function (o, k) {
      o.setAttribute('aria-checked', String(k === mIdx));
    });
  };

  Preview.prototype.bindText = function () {
    if (!this.textIn) return;
    var self = this;
    this.textIn.addEventListener('input', function () {
      var v = self.textIn.value;
      if (self.cfg.textUppercase) { v = v.toUpperCase(); self.textIn.value = v; }
      self.state.text = v;
      self.textIn.classList.remove('is-invalid');
      if (self.textCount) self.textCount.textContent = v.length;
      self.textOut.textContent = v;
      self.writeProps();
    });
  };

  /* ----- image urls ----- */
  Preview.prototype.url = function (name) {
    return this.cfg.filesRoot + name + '?width=' + (this.cfg.imageWidth || 1200);
  };

  Preview.prototype.baseUrl = function () {
    var p = this.cfg.prefix, c = this.state.count, m = this.state.metal;
    return this.url(p + '-base-' + (m ? m + '-' : '') + c + '.' + (this.cfg.baseExt || 'jpg'));
  };

  Preview.prototype.slotFor = function (i) {
    var c = this.state.count;
    return (this.layout[c] && this.layout[c][i]) || c + '-' + (i + 1);
  };

  Preview.prototype.stoneUrl = function (i) {
    var m = this.months[this.state.stones[i]];
    if (!m) return null;
    if (this.cfg.mode === 'tint') return this.url(this.cfg.prefix + '-mask-' + this.slotFor(i) + '.png');
    return this.url(this.cfg.prefix + '-stone-' + this.slotFor(i) + '-' + m.key + '.png');
  };

  /* =====================================================================
   * VARIANT PHOTO MODE
   * Builds multi-stone previews from the product's existing month photos
   * (one pendant per photo, all shot identically on a plain background).
   * Each photo is analysed once: the pendant is cut out, the chain lines are
   * traced, and the pendants are re-hung along the chain. No uploads needed.
   * ===================================================================== */
  var photoCache = {};

  function loadImg(url) {
    return new Promise(function (resolve, reject) {
      var i = new Image();
      i.crossOrigin = 'anonymous';
      i.onload = function () { resolve(i); };
      i.onerror = reject;
      i.src = url;
    });
  }

  function analysePhoto(img, threshold) {
    var W = img.naturalWidth, H = img.naturalHeight;
    var c = document.createElement('canvas');
    c.width = W; c.height = H;
    var x = c.getContext('2d');
    x.drawImage(img, 0, 0);
    var d = x.getImageData(0, 0, W, H).data; // throws if the image is not CORS-readable
    var k0 = (4 * W + 4) * 4;
    var bg = [d[k0], d[k0 + 1], d[k0 + 2]];
    var T = threshold || 36;
    function on(i, j) {
      var k = (j * W + i) * 4;
      return Math.abs(d[k] - bg[0]) + Math.abs(d[k + 1] - bg[1]) + Math.abs(d[k + 2] - bg[2]) > T;
    }
    function runs(j) {
      var out = [], s = -1, i;
      for (i = 0; i < W; i++) {
        var o = on(i, j);
        if (o && s < 0) s = i;
        if (!o && s >= 0) { out.push([s, i - 1]); s = -1; }
      }
      if (s >= 0) out.push([s, W - 1]);
      var m = [];
      out.forEach(function (r) { if (m.length && r[0] - m[m.length - 1][1] <= 3) m[m.length - 1][1] = r[1]; else m.push(r.slice()); });
      return m;
    }
    var bottom = H - 1;
    while (bottom > 0 && !runs(bottom).length) bottom--;
    var br = runs(Math.max(0, bottom - 4));
    if (!br.length) throw new Error('no pendant found');
    var widest = br.reduce(function (a, r) { return r[1] - r[0] > a[1] - a[0] ? r : a; }, br[0]);
    var cx = Math.round((widest[0] + widest[1]) / 2);

    // walk up from the bottom until the column under the pendant centre is empty (gap between the chain lines)
    var spans = {}, top = bottom, miss = 0, j;
    for (j = bottom; j > 0; j--) {
      var r = runs(j).filter(function (r) { return r[0] <= cx + 4 && r[1] >= cx - 4; })[0];
      if (!r) { if (++miss >= 4) { top = j + 4; break; } continue; }
      miss = 0; spans[j] = r; top = j;
    }
    // trim the chain links that merge into the top of the bail: measure the bail lower down,
    // where it is clear of the chain, then clip the upper rows to that width
    var ph = bottom - top, core = [];
    var t1 = top + Math.round(ph * 0.19), t2 = top + Math.round(ph * 0.34);
    for (j = t1; j < t2; j++) if (spans[j]) core.push(spans[j]);
    if (core.length) {
      var cl = Math.min.apply(null, core.map(function (s) { return s[0]; }));
      var cr = Math.max.apply(null, core.map(function (s) { return s[1]; }));
      for (j = top; j < t1; j++) if (spans[j]) {
        if (spans[j][1] < cl || spans[j][0] > cr) { delete spans[j]; continue; }
        spans[j] = [Math.max(spans[j][0], cl - 1), Math.min(spans[j][1], cr + 1)];
      }
    }
    var x0 = W, x1 = 0, dropW = 0;
    for (j = top; j <= bottom; j++) if (spans[j]) {
      x0 = Math.min(x0, spans[j][0]); x1 = Math.max(x1, spans[j][1]);
      if (j > top + (bottom - top) * 0.55) dropW = Math.max(dropW, spans[j][1] - spans[j][0]);
    }
    // trace the two chain lines above the pendant
    var left = [], right = [];
    for (j = top - 6; j > Math.max(0, top - H * 0.45); j -= 2) {
      var rs = runs(j);
      var l = rs.filter(function (r) { return r[1] < cx; }), rr = rs.filter(function (r) { return r[0] > cx; });
      if (l.length) left.push([j, (l[l.length - 1][0] + l[l.length - 1][1]) / 2]);
      if (rr.length) right.push([j, (rr[0][0] + rr[0][1]) / 2]);
    }
    var cc = [0, 0, 0], n = 0;
    left.slice(0, 40).forEach(function (p) {
      var k = (p[0] * W + Math.round(p[1])) * 4;
      cc[0] += d[k]; cc[1] += d[k + 1]; cc[2] += d[k + 2]; n++;
    });
    if (n) cc = cc.map(function (v) { return Math.round(v / n); });

    // cut-out of the pendant with soft edges
    var cw = x1 - x0 + 1, ch = bottom - top + 1;
    var cut = document.createElement('canvas');
    cut.width = cw; cut.height = ch;
    var cx2 = cut.getContext('2d');
    var outImg = cx2.createImageData(cw, ch), o = outImg.data;
    for (j = top; j <= bottom; j++) {
      var s = spans[j];
      if (!s) continue;
      for (var i = s[0]; i <= s[1]; i++) {
        var k = (j * W + i) * 4, q = ((j - top) * cw + (i - x0)) * 4;
        o[q] = d[k]; o[q + 1] = d[k + 1]; o[q + 2] = d[k + 2];
        var edge = Math.min(i - s[0], s[1] - i), al = 255;
        if (edge < 2) {
          var dd = Math.abs(d[k] - bg[0]) + Math.abs(d[k + 1] - bg[1]) + Math.abs(d[k + 2] - bg[2]);
          al = Math.max(0, Math.min(255, (dd - 6) * 5));
        }
        o[q + 3] = al;
      }
    }
    cx2.putImageData(outImg, 0, 0);

    // the same photo with its pendant removed = empty chain
    var base = document.createElement('canvas');
    base.width = W; base.height = H;
    var bx = base.getContext('2d');
    bx.drawImage(c, 0, 0);
    bx.fillStyle = 'rgb(' + bg.join(',') + ')';
    for (j = top - 2; j <= bottom + 3; j++) {
      var sp = spans[j] || spans[j + 2] || spans[j - 2];
      if (sp) bx.fillRect(sp[0] - 3, j, sp[1] - sp[0] + 7, 1);
    }
    return { W: W, H: H, bg: bg, top: top, bottom: bottom, cx: cx, x0: x0, x1: x1, dropW: dropW || cw, left: left, right: right, chain: cc, cut: cut, base: base, src: c };
  }

  function photo(url, threshold) {
    if (!photoCache[url]) {
      photoCache[url] = loadImg(url).then(function (img) { return analysePhoto(img, threshold); });
      photoCache[url].catch(function () { delete photoCache[url]; });
    }
    return photoCache[url];
  }

  function chainY(a, side, xq) {
    var L = side < 0 ? a.left : a.right, best = null;
    for (var i = 0; i < L.length; i++) if (!best || Math.abs(L[i][1] - xq) < Math.abs(best[1] - xq)) best = L[i];
    return best ? best[0] : a.top;
  }

  // Crop a circle-ready square of just the stone from a pendant photo (bail on top, stone below).
  // region limits the search (used for variant photos, where the chain is also in the picture).
  function gemCrop(canvas, bg, region) {
    var W = canvas.width, H = canvas.height;
    var r = region || { x0: 0, x1: W - 1, top: 0, bottom: H - 1 };
    var x0 = Math.max(0, r.x0), x1 = Math.min(W - 1, r.x1), y0 = Math.max(0, r.top), y1 = Math.min(H - 1, r.bottom);
    var rw = x1 - x0 + 1, rh = y1 - y0 + 1;
    var d = canvas.getContext('2d').getImageData(x0, y0, rw, rh).data;
    if (!bg) bg = [d[0], d[1], d[2]];
    function on(x, y) {
      var k = (y * rw + x) * 4;
      return Math.abs(d[k] - bg[0]) + Math.abs(d[k + 1] - bg[1]) + Math.abs(d[k + 2] - bg[2]) > 110;
    }
    function span(y) {
      var l = -1, q = -1;
      for (var x = 0; x < rw; x++) if (on(x, y)) { if (l < 0) l = x; q = x; }
      return l < 0 ? null : [l, q];
    }
    var top = -1, bot = -1, y;
    for (y = 0; y < rh; y++) if (span(y)) { if (top < 0) top = y; bot = y; }
    if (top < 0) return null;
    var h = bot - top, neck = top + Math.round(h * 0.35), best = 1e9;
    for (y = top + Math.round(h * 0.35); y < top + Math.round(h * 0.6); y++) {
      var sp = span(y);
      if (sp && sp[1] - sp[0] < best) { best = sp[1] - sp[0]; neck = y; }
    }
    var l = rw, q = 0, t = rh, b = 0;
    for (y = neck; y <= bot; y++) {
      var s2 = span(y);
      if (!s2) continue;
      l = Math.min(l, s2[0]); q = Math.max(q, s2[1]); t = Math.min(t, y); b = y;
    }
    var dw = q - l, dh = b - t;
    if (dw < 8 || dh < 8) return null;
    var side = dw * 0.62, cx = x0 + (l + q) / 2 - dw * 0.05, cy = y0 + t + dh * 0.57;
    var out = document.createElement('canvas');
    out.width = out.height = 96;
    var x = out.getContext('2d');
    x.fillStyle = 'rgb(' + bg.join(',') + ')';
    x.fillRect(0, 0, 96, 96);
    x.drawImage(canvas, cx - side / 2, cy - side / 2, side, side, 0, 0, 96, 96);
    return out.toDataURL('image/jpeg', 0.88);
  }

  function swatchFromUpload(url) {
    return loadImg(url).then(function (img) {
      var c = document.createElement('canvas');
      c.width = img.naturalWidth; c.height = img.naturalHeight;
      c.getContext('2d').drawImage(img, 0, 0);
      var u = gemCrop(c, null, null);
      if (!u) throw new Error('no stone found');
      return u;
    });
  }

  function swatchFromVariant(a) {
    return gemCrop(a.src, a.bg, { x0: a.x0 - 6, x1: a.x1 + 6, top: a.top, bottom: a.bottom });
  }

  // Close-up pendant exported on a flat background (Canva cut-out on #f2f2f2): key out the flat colour.
  var pendCache = {};
  function pendantCut(img) {
    var W = img.naturalWidth, H = img.naturalHeight;
    var c = document.createElement('canvas'); c.width = W; c.height = H;
    var x = c.getContext('2d'); x.drawImage(img, 0, 0);
    var d = x.getImageData(0, 0, W, H).data, n = W * H, i;
    var bg = [d[0], d[1], d[2]];
    function diff(k) { return Math.abs(d[k] - bg[0]) + Math.abs(d[k + 1] - bg[1]) + Math.abs(d[k + 2] - bg[2]); }
    var out = new Uint8Array(n), q = new Int32Array(n), h = 0, t = 0;
    function seed(j) { if (!out[j] && diff(j * 4) <= 8) { out[j] = 1; q[t++] = j; } }
    for (i = 0; i < W; i++) { seed(i); seed((H - 1) * W + i); }
    for (i = 0; i < H; i++) { seed(i * W); seed(i * W + W - 1); }
    while (h < t) {
      var j = q[h++], y = (j / W) | 0, xx = j % W;
      if (xx > 0) seed(j - 1); if (xx < W - 1) seed(j + 1); if (y > 0) seed(j - W); if (y < H - 1) seed(j + W);
    }
    var x0 = W, x1 = 0, y0 = H, y1 = 0, yy, xq;
    for (yy = 0; yy < H; yy++) for (xq = 0; xq < W; xq++) if (!out[yy * W + xq]) {
      if (xq < x0) x0 = xq; if (xq > x1) x1 = xq; if (yy < y0) y0 = yy; if (yy > y1) y1 = yy;
    }
    if (x1 <= x0 || y1 <= y0) throw new Error('empty pendant');
    var cw = x1 - x0 + 1, ch = y1 - y0 + 1;
    var cut = document.createElement('canvas'); cut.width = cw; cut.height = ch;
    var cx = cut.getContext('2d'), o = cx.createImageData(cw, ch);
    var dropW = 0, sx = 0, sn = 0;
    for (yy = y0; yy <= y1; yy++) {
      var l = -1, r = -1;
      for (xq = x0; xq <= x1; xq++) {
        var jj = yy * W + xq, k = jj * 4, qq = ((yy - y0) * cw + (xq - x0)) * 4;
        if (out[jj]) continue;
        if (l < 0) l = xq; r = xq;
        o.data[qq] = d[k]; o.data[qq + 1] = d[k + 1]; o.data[qq + 2] = d[k + 2];
        var edge = (xq > 0 && out[jj - 1]) || (xq < W - 1 && out[jj + 1]) || (yy > 0 && out[jj - W]) || (yy < H - 1 && out[jj + W]);
        o.data[qq + 3] = edge ? Math.max(40, Math.min(255, diff(k) * 6)) : 255;
        if (yy < y0 + ch * 0.15) { sx += xq - x0; sn++; }
      }
      if (l >= 0 && yy > y0 + ch * 0.55) dropW = Math.max(dropW, r - l);
    }
    cx.putImageData(o, 0, 0);
    return { cut: cut, w: cw, h: ch, dropW: dropW || cw, bailCx: sn ? sx / sn : cw / 2, closeup: true };
  }

  function pendant(url) {
    if (!pendCache[url]) pendCache[url] = loadImg(url).then(pendantCut); // failures stay cached: no repeat 404s
    return pendCache[url];
  }

  Preview.prototype.pendantFor = function (metal, mIdx) {
    if (!this.cfg.pendants) return Promise.resolve(null);
    return pendant(this.fileUrl(this.cfg.pendants, metal, mIdx, 900)).catch(function () { return null; });
  };

  /* ----- two-stone split pieces (e.g. Coeur Toi & Moi): left half from one photo, right half from another ----- */
  var plainCache = {};
  function photoPlain(url) {
    if (!plainCache[url]) {
      plainCache[url] = loadImg(url).then(function (img) {
        var c = document.createElement('canvas'); c.width = img.naturalWidth; c.height = img.naturalHeight;
        c.getContext('2d').drawImage(img, 0, 0); return c;
      });
      plainCache[url].catch(function () { delete plainCache[url]; });
    }
    return plainCache[url];
  }

  // Which photo shows month mIdx, and on which side. With {pair} photos (jan-feb, mar-apr …) the odd
  // months sit on the left and the even months on the right; the other side is made by mirroring.
  // Without mirroring (stones that are not symmetrical, e.g. Pluie) the other side comes from a
  // swapped photo: feb-jan, apr-mar … where the even month sits on the left.
  Preview.prototype.splitPhotoFor = function (metal, mIdx, want) {
    var pat = this.cfg.photos;
    if (pat && pat.indexOf('{pair') >= 0) {
      var natural = mIdx % 2 ? 'R' : 'L';
      if (want && want !== natural && this.cfg.splitMirror === false) return { url: this.fileUrl(pat, metal, mIdx, 1600, true), side: want };
      return { url: this.fileUrl(pat, metal, mIdx, 1600), side: natural };
    }
    if (pat) return { url: this.fileUrl(pat, metal, mIdx, 1600), side: 'both' };
    var v = this.variantImage(metal, mIdx) || this.variantImage(null, mIdx);
    return v ? { url: v.replace(/width=\d+/, 'width=1600'), side: 'both' } : null;
  };

  // Draws the half that shows the stone onto the wanted side (mirrored if it sits on the other side).
  function drawHalf(x, src, have, want, seam, W, H, path) {
    x.save();
    x.beginPath();
    if (path && path.length > 1) { // join that follows the gap between the stones: [[y, x], …] as fractions
      var edge = want === 'L' ? 0 : W;
      x.moveTo(edge, 0);
      x.lineTo(path[0][1] * W, 0);
      path.forEach(function (pt) { x.lineTo(pt[1] * W, pt[0] * H); });
      x.lineTo(path[path.length - 1][1] * W, H);
      x.lineTo(edge, H);
      x.closePath();
    } else if (want === 'L') x.rect(0, 0, seam, H); else x.rect(seam, 0, W - seam, H);
    x.clip();
    if (have === 'both' || have === want) x.drawImage(src, 0, 0, W, H);
    else { x.translate(2 * seam, 0); x.scale(-1, 1); x.drawImage(src, 0, 0, W, H); }
    x.restore();
  }

  // Swatch for a two-stone ring: a square around the stone on that side.
  // The stone centre sits splitStone[0] × width from the seam, at splitStone[1] × height.
  function splitSwatch(c, side, at, pos) {
    var W = c.width, H = c.height, seam = W * (at || 0.5);
    var off = (pos && pos[0]) || 0.1, cy = H * ((pos && pos[1]) || 0.5), s = W * ((pos && pos[2]) || 0.09);
    var cx = side === 'R' ? seam + off * W : seam - off * W;
    var out = document.createElement('canvas'); out.width = out.height = 96;
    out.getContext('2d').drawImage(c, cx - s / 2, cy - s / 2, s, s, 0, 0, 96, 96);
    return out.toDataURL('image/jpeg', 0.88);
  }

  Preview.prototype.renderSplit = function (token) {
    var self = this, metal = this.state.metal;
    var a = this.splitPhotoFor(metal, this.state.stones[0], 'L'), b = this.splitPhotoFor(metal, this.state.stones[1], 'R');
    if (!a || !b) return Promise.reject(new Error('missing split photo'));
    return Promise.all([photoPlain(a.url), photoPlain(b.url)]).then(function (res) {
      if (token !== self.renderToken) return;
      var A = res[0], B = res[1], W = A.width, H = A.height, seam = W * (self.cfg.splitAt || 0.5);
      var cv = self.canvas;
      if (!cv) { cv = self.canvas = el('canvas', 'jmlp__canvas', { role: 'img' }); self.stage.insertBefore(cv, self.layersEl); }
      cv.width = W; cv.height = H;
      cv.setAttribute('aria-label', self.recipe().stones.map(function (s) { return s.label; }).join(', '));
      var x = cv.getContext('2d');
      drawHalf(x, A, a.side, 'L', seam, W, H, self.cfg.splitPath);
      drawHalf(x, B, b.side, 'R', seam, W, H, self.cfg.splitPath);
      // Square used for the scroll mini-preview and the cart thumbnail: centred on the stones
      // (splitCfg.stone y), or splitCfg.focus = [centreX, centreY, size] as fractions when set.
      var fz = (self.cfg.splitCfg && self.cfg.splitCfg.focus) || null, st = self.cfg.splitStone || [0.1, 0.5, 0.09];
      var fs = Math.min(W, H, W * ((fz && fz[2]) || 0.6));
      var fcx = fz ? W * fz[0] : seam, fcy = fz ? H * fz[1] : H * (st[1] || 0.5);
      self.focus = { x: Math.max(0, Math.min(W - fs, fcx - fs / 2)), y: Math.max(0, Math.min(H - fs, fcy - fs / 2)), s: fs };
      self.paintCopies();
      self.stage.classList.add('jmlp__stage--canvas');
    });
  };

  Preview.prototype.variantImage = function (metal, mIdx) {
    if (this.monthIdx >= 0) {
      var vs = this.cfg.product.variants;
      for (var i = 0; i < vs.length; i++) {
        var v = vs[i];
        if (!v.image) continue;
        if (metal && this.metalIdx >= 0 && this.metalFromVariant(v) !== metal) continue;
        if (this.monthIndexOf(v.options[this.monthIdx]) === mIdx) return v.image;
      }
    }
    var bm = this.mediaPhotos && this.mediaPhotos.found ? this.mediaPhotos.byMetal : null;
    if (!bm) return null;
    if (metal) return (bm[metal] && bm[metal][mIdx]) || (bm._any && bm._any[mIdx]) || null;
    var keys = Object.keys(bm);
    for (var k = 0; k < keys.length; k++) if (bm[keys[k]][mIdx]) return bm[keys[k]][mIdx];
    return null;
  };

  Preview.prototype.renderVariantPhotos = function (token) {
    var self = this;
    var urls = [];
    for (var i = 0; i < this.state.count; i++) urls.push(this.variantImage(this.state.metal, this.state.stones[i]));
    if (urls.some(function (u) { return !u; })) return Promise.reject(new Error('missing month photo'));
    var t = this.cfg.photoThreshold, metal = this.state.metal;
    var jobs = urls.map(function (u, i) {
      return self.pendantFor(metal, self.state.stones[i]).then(function (pc) { return pc || photo(u, t); });
    });
    return Promise.all([photo(urls[0], t)].concat(jobs)).then(function (res) {
      if (token !== self.renderToken) return;
      var A = res[0], parts = res.slice(1), n = parts.length, w = A.dropW;
      var spread = (self.cfg.photoSpread || 2.1) * w;
      var sp = Math.min((self.cfg.photoGap || 0.92) * w, n > 1 ? spread / (n - 1) : w);
      var tilt = self.cfg.photoTilt == null ? 0.1 : self.cfg.photoTilt;
      var cv = self.canvas;
      if (!cv) {
        cv = self.canvas = el('canvas', 'jmlp__canvas', { role: 'img' });
        self.stage.insertBefore(cv, self.layersEl);
      }
      cv.width = A.W; cv.height = A.H;
      cv.setAttribute('aria-label', self.recipe().stones.map(function (s) { return s.label; }).join(', '));
      var x = cv.getContext('2d');
      x.drawImage(A.base, 0, 0);
      var pos = parts.map(function (p, i) {
        var k = i - (n - 1) / 2, hx = A.cx + k * sp;
        return { p: p, k: k, hx: hx, hy: k === 0 ? A.top : chainY(A, k < 0 ? -1 : 1, hx) };
      });
      if (n % 2 === 0) { // short length of chain between the two middle pendants
        var L = pos[n / 2 - 1], R = pos[n / 2];
        x.strokeStyle = 'rgb(' + A.chain.join(',') + ')';
        x.lineWidth = Math.max(2, A.W / 240);
        x.beginPath();
        x.moveTo(L.hx, L.hy + 4);
        x.quadraticCurveTo(A.cx, Math.max(L.hy, R.hy) + A.H / 66, R.hx, R.hy + 4);
        x.stroke();
      }
      pos.slice().sort(function (a, b) { return Math.abs(b.k) - Math.abs(a.k); }).forEach(function (q) {
        x.save();
        x.translate(q.hx, q.hy);
        x.rotate(-q.k * tilt);
        if (q.p.closeup) {
          var sc = w / q.p.dropW;
          x.drawImage(q.p.cut, -q.p.bailCx * sc, 0, q.p.w * sc, q.p.h * sc);
        } else {
          x.drawImage(q.p.cut, -(q.p.cx - q.p.x0), 0);
        }
        x.restore();
      });
      // square around the pendants, for the floating mini preview and the gallery thumbnail
      var span = (n - 1) * sp + w * 1.6, fs = Math.min(A.W, Math.max(span, A.H * 0.42));
      var fcy = A.top + (A.H - A.top) * 0.3;
      self.focus = { x: Math.max(0, A.cx - fs / 2), y: Math.max(0, Math.min(A.H - fs, fcy - fs / 2)), s: fs };
      self.paintCopies();
      self.stage.classList.add('jmlp__stage--canvas');
    });
  };

  /* ----- render ----- */
  Preview.prototype.render = function () {
    var self = this;
    var token = ++this.renderToken;
    var count = this.state.count;

    if (this.photoMode) {
      this.loading.hidden = false;
      (this.split ? this.renderSplit(token) : this.renderVariantPhotos(token)).catch(function (e) {
        // fall back to the plain variant photo
        if (self.cfg.debug || (window.Shopify && window.Shopify.designMode)) console.warn('[JM Live Preview]', e);
        self.stage.classList.remove('jmlp__stage--canvas');
        var v = self.variantById(self.state.variantId);
        if (v && v.image) self.base.src = v.image;
        else if (self.cfg.fallbackImage) self.base.src = self.cfg.fallbackImage;
      }).then(function () { if (token === self.renderToken) self.loading.hidden = true; });
      this.writeProps();
      this.root.dispatchEvent(new CustomEvent('jmlp:change', { bubbles: true, detail: this.recipe() }));
      return;
    }

    // base photo: try the layered file, fall back to the variant photo
    var baseWanted = this.baseUrl();
    var v = this.variantById(this.state.variantId);
    var fallback = (v && v.image) || this.cfg.fallbackImage;
    this.loading.hidden = false;
    preload(baseWanted)
      .catch(function () { return fallback ? preload(fallback) : Promise.reject(); })
      .then(function (u) { if (token === self.renderToken && self.base.src !== u) self.base.src = u; })
      .catch(function () {})
      .then(function () { if (token === self.renderToken) self.loading.hidden = true; });

    // stone layers (re-use DOM nodes to avoid flicker)
    while (this.layersEl.children.length > count) this.layersEl.removeChild(this.layersEl.lastChild);
    for (var i = 0; i < count; i++) {
      var layer = this.layersEl.children[i];
      if (!layer) {
        layer = el('div', 'jmlp__layer');
        var img = el('img', 'jmlp__layer-img', { alt: '', crossorigin: 'anonymous' });
        img.src = BLANK;
        layer.appendChild(img);
        layer.appendChild(el('span', 'jmlp__tint'));
        this.layersEl.appendChild(layer);
      }
      this.paintLayer(layer, i, token);
    }

    this.writeProps();
    this.preloadNeighbours();
    this.root.dispatchEvent(new CustomEvent('jmlp:change', { bubbles: true, detail: this.recipe() }));
  };

  Preview.prototype.paintLayer = function (layer, i, token) {
    var self = this;
    var url = this.stoneUrl(i);
    var img = layer.firstChild;
    var tint = layer.lastChild;
    var m = this.months[this.state.stones[i]] || {};
    layer.classList.toggle('jmlp__layer--tint', this.cfg.mode === 'tint');
    tint.style.setProperty('--tint', m.color || 'transparent');
    preload(url).then(function (u) {
      if (token !== self.renderToken) return;
      img.src = u;
      img.hidden = false;
      if (self.cfg.mode === 'tint') {
        var abs = 'url("' + new URL(u, location.href).href + '")';
        tint.style.webkitMaskImage = abs;
        tint.style.maskImage = abs;
      }
    }).catch(function () {
      if (token !== self.renderToken) return;
      img.src = BLANK;
      img.hidden = true;
      if (window.Shopify && window.Shopify.designMode) console.warn('[JM Live Preview] missing layer:', url);
    });
  };

  Preview.prototype.preloadNeighbours = function () {
    if (this.cfg.mode === 'tint' || (navigator.connection && navigator.connection.saveData)) return;
    var self = this;
    var idle = window.requestIdleCallback || function (cb) { return setTimeout(cb, 300); };
    idle(function () {
      for (var i = 0; i < self.state.count; i++) {
        var slot = self.slotFor(i);
        self.months.forEach(function (m) {
          preload(self.url(self.cfg.prefix + '-stone-' + slot + '-' + m.key + '.png')).catch(function () {});
        });
      }
    });
  };

  /* ----- cart properties ----- */
  Preview.prototype.recipe = function () {
    var self = this;
    var stones = [];
    for (var i = 0; i < this.state.count; i++) stones.push(this.months[this.state.stones[i]]);
    return {
      variantId: this.state.variantId,
      metal: this.state.metal,
      count: this.state.count,
      stones: stones.map(function (m, i) { return { position: i + 1, key: m.key, month: m.month, stone: m.stone, label: self.labelFor(self.state.stones[i]) }; }),
      text: this.state.text
    };
  };

  Preview.prototype.writeProps = function () {
    if (!this.propsBoxes || !this.propsBoxes.length) return;
    var self = this;
    var r = this.recipe();
    var name = this.cfg.propertyName || 'Birthstone';
    var pairs = [];
    function add(k, v) { pairs.push([k, v]); }
    r.stones.forEach(function (s) {
      // stone 1 is already in the variant title when the product has a Birthstone (month) variant option
      if (s.position === 1 && self.monthIdx >= 0) return;
      add(r.count > 1 ? name + ' ' + s.position : name, s.label);
    });
    if (this.cfg.textEnabled && r.text.trim()) add(this.cfg.textProperty || 'Engraving', r.text.trim());
    // hidden (underscore) property: lets the order / a future cart thumbnail rebuild the exact preview
    var code = [r.metal || '-', r.count, r.stones.map(function (s) { return s.key; }).join('.'), r.text].join('|');
    add('_preview', code);
    // hidden link for the team: opens the product with this exact design
    add('_Design preview', location.origin + location.pathname + '?variant=' + r.variantId + '&design=' + encodeURIComponent(code));
    this.previewCode = code;
    this.propsBoxes.forEach(function (box) {
      box.innerHTML = '';
      pairs.forEach(function (p) {
        var i = document.createElement('input');
        i.type = 'hidden';
        i.name = 'properties[' + p[0] + ']';
        i.value = p[1];
        box.appendChild(i);
      });
    });
  };

  /* ----- flatten to an image (for downloads, cart thumbnails or an upload endpoint later) ----- */
  Preview.prototype.capture = function (type, quality) {
    var self = this;
    if (this.canvas && this.stage.classList.contains('jmlp__stage--canvas')) {
      return new Promise(function (resolve) { self.canvas.toBlob(resolve, type || 'image/jpeg', quality || 0.9); });
    }
    var base = this.base;
    var w = base.naturalWidth || 1200, h = base.naturalHeight || 1200;
    var canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    var ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(base, 0, 0, w, h);
    Array.prototype.forEach.call(this.layersEl.children, function (layer) {
      var img = layer.firstChild;
      if (img.hidden || !img.complete || img.src === BLANK) return;
      if (self.cfg.mode === 'tint') {
        var off = document.createElement('canvas');
        off.width = w; off.height = h;
        var o = off.getContext('2d');
        o.drawImage(img, 0, 0, w, h);
        o.globalCompositeOperation = 'multiply';
        o.fillStyle = layer.lastChild.style.getPropertyValue('--tint');
        o.fillRect(0, 0, w, h);
        o.globalCompositeOperation = 'destination-in';
        o.drawImage(img, 0, 0, w, h);
        ctx.drawImage(off, 0, 0);
      } else {
        ctx.drawImage(img, 0, 0, w, h);
      }
    });
    if (this.textOut && this.state.text) {
      var cs = getComputedStyle(this.textOut);
      var scale = w / this.stage.clientWidth;
      ctx.save();
      ctx.translate(w * (parseFloat(cs.getPropertyValue('--jmlp-text-x')) / 100), h * (parseFloat(cs.getPropertyValue('--jmlp-text-y')) / 100));
      ctx.rotate((parseFloat(cs.getPropertyValue('--jmlp-text-rotate')) || 0) * Math.PI / 180);
      ctx.font = cs.fontStyle + ' ' + cs.fontWeight + ' ' + parseFloat(cs.fontSize) * scale + 'px ' + cs.fontFamily;
      ctx.fillStyle = cs.color;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(this.state.text, 0, 0);
      ctx.restore();
    }
    return new Promise(function (resolve) { canvas.toBlob(resolve, type || 'image/jpeg', quality || 0.9); });
  };

  /* ----- cart & mini-cart: swap the stock photo for the customer's design ----- */
  var thumbStore = {
    get: function (k) { try { var m = JSON.parse(localStorage.getItem('jmlp-thumbs') || '{}'); return m[k] && m[k].u; } catch (e) { return null; } },
    set: function (k, u) {
      try {
        var m = JSON.parse(localStorage.getItem('jmlp-thumbs') || '{}');
        m[k] = { u: u, t: Date.now() };
        var keys = Object.keys(m).sort(function (a, b) { return m[b].t - m[a].t; });
        keys.slice(20).forEach(function (old) { delete m[old]; });
        localStorage.setItem('jmlp-thumbs', JSON.stringify(m));
      } catch (e) {}
    }
  };

  var cartBusy = false, cartAgain = false;
  function cartThumbs() {
    if (cartBusy) { cartAgain = true; return; }
    var rows = document.querySelectorAll('[id^="CartItem-"], [id^="CartDrawer-Item-"], .mini-cart__navigation > li[data-variant-id]');
    if (!rows.length) return;
    cartBusy = true;
    fetch((window.Shopify && Shopify.routes && Shopify.routes.root || '/') + 'cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        Array.prototype.forEach.call(rows, function (row) {
          var idx = -1, m = (row.id || '').match(/-(\d+)$/);
          if (m) idx = parseInt(m[1], 10) - 1;
          else { var p = row.parentElement; var sib = Array.prototype.filter.call(p.children, function (c) { return c.hasAttribute('data-variant-id'); }); idx = sib.indexOf(row); }
          var item = cart.items[idx];
          if (!item || !item.properties || !item.properties._preview) return;
          var u = thumbStore.get(item.variant_id + '|' + item.properties._preview);
          if (!u) return;
          var img = row.querySelector('img');
          if (!img || img.getAttribute('data-jmlp-thumb') === u.slice(-40)) return;
          img.removeAttribute('srcset'); img.removeAttribute('data-srcset');
          img.src = u; img.setAttribute('data-jmlp-thumb', u.slice(-40));
        });
      })
      .catch(function () {})
      .then(function () { cartBusy = false; if (cartAgain) { cartAgain = false; setTimeout(cartThumbs, 50); } });
  }
  var cartTimer;
  function watchCart() {
    cartThumbs();
    new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var t = muts[i].target;
        if (t.closest && t.closest('cart-items, mini-cart, cart-drawer, #main-cart-items, .mini-cart')) {
          clearTimeout(cartTimer); cartTimer = setTimeout(cartThumbs, 250); return;
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  // exposed for troubleshooting from the browser console
  instances._tools = { Preview: Preview, photo: photo, gemCrop: gemCrop, swatchFromUpload: swatchFromUpload, swatchFromVariant: swatchFromVariant };

  /* ---------- boot ---------- */
  function boot(scope) {
    (scope || document).querySelectorAll('[data-jmlp]').forEach(function (root) {
      if (root.__jmlp) return;
      try { root.__jmlp = new Preview(root); } catch (e) { console.error('[JM Live Preview]', e); }
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(); watchCart(); });
  else { boot(); watchCart(); }
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
